import clsx from 'clsx';
import React, { FC, useEffect, useRef, useState } from 'react';
import UploaderContent from './UploaderContent';
import UploadError from './UploadError';
import Attachment from './Attachment';
import LinearDashedBorder from './LinearDashedBorder';
import { useMutation } from 'react-query';
import { deleteWhiteLabelAsset, uploadWhiteLabelAssets } from 'api/brand/api';
import { WhiteLabelAssetMinimal } from 'api/brand/schema';
import { subDomain } from 'api/brand/utils';
import { GraniteLabel } from 'components/V2/Label/GraniteLabel';

interface UploaderProps {
  onChange: (file?: WhiteLabelAssetMinimal | null) => void;
  uploadedFile: WhiteLabelAssetMinimal | null;
  assetType: string;
  error?: string;
  label?: string;
}

const Uploader: FC<UploaderProps> = ({
  onChange,
  assetType,
  uploadedFile,
  error,
  label,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [errorUploadError, setUploadError] = useState<boolean | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB in bytes
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/svg+xml'];

  const validateFile = (file: File): boolean | null => {
    if (!ALLOWED_TYPES.includes(file.type)) return true;
    if (file.size > MAX_FILE_SIZE) return true;
    return false;
  };

  const { mutate: uploadAsset, reset } = useMutation(uploadWhiteLabelAssets, {
    onSuccess: (data) => {
      onChange({
        asset_name: data.asset_name,
        id: data.id,
        url: process.env.NODE_ENV === 'development' ? data.s3_url : data.url,
        file_size: data.file_size,
      });
    },
    onError: () => {
      setUploadError(true);
      onClearFile();
    },
  });

  const { mutate: deleteAsset } = useMutation(deleteWhiteLabelAsset, {
    onSuccess: () => {
      setUploadError(false);
      onClearFile();
    },
  });

  useEffect(() => {
    if (uploadedFile === null) {
      setFile(null);
      setUploadError(false);
      reset();
      if (inputRef.current) inputRef.current.value = '';
    }
  }, [uploadedFile, reset]);

  const onFileChange = async (file: File) => {
    if (file) {
      const validationError = validateFile(file);
      if (validationError) {
        setUploadError(true);
        setFile(null);
        if (inputRef.current) inputRef.current.value = '';
        return;
      }
      const formData = new FormData();
      formData.append('file', file);
      formData.append('asset_type', assetType);
      formData.append('company_slug', subDomain);
      setFile(file);
      uploadAsset(formData);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onFileChange(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      const file = droppedFiles[0];
      if (file) onFileChange(file);
    }
  };

  const onClearFile = () => {
    setFile(null);
    onChange(null);
    reset();
    if (inputRef.current) inputRef.current.value = '';
  };

  const onClear = () => {
    if (uploadedFile?.id) {
      deleteAsset(uploadedFile.id);
      setUploadError(false);
      onClearFile();
    }
  };

  const onTryAgain = () => {
    onClearFile();
    setUploadError(false);
  };

  return (
    <div>
      {label ? (
        <GraniteLabel>
          <span className="mb-2 text-base font-bold text-content-base-subdued">
            {label}
          </span>
        </GraniteLabel>
      ) : null}
      <div
        className={clsx(
          'group relative h-[200px] cursor-pointer overflow-hidden rounded bg-[linear-gradient(97.76deg,_rgba(126,_119,_179,_0.08)_3.17%,_rgba(137,_137,_255,_0.08)_26.59%,_rgba(108,_157,_255,_0.08)_49.93%,_rgba(120,_207,_218,_0.08)_73.45%,_rgba(159,_244,_169,_0.08)_96.88%)]',
        )}
        {...(!errorUploadError &&
          !file &&
          !uploadedFile && {
            onDragOver: handleDragOver,
            onDragLeave: handleDragLeave,
            onDrop: handleDrop,
            onClick: () => inputRef.current?.click(),
          })}
      >
        <input
          type="file"
          className="hidden"
          ref={inputRef}
          onChange={handleFileChange}
          accept=".png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml"
        />
        <LinearDashedBorder />
        <div className="flex h-full flex-col items-center justify-center p-8">
          {!errorUploadError && !file && !uploadedFile && <UploaderContent />}
          {errorUploadError && <UploadError onClear={onTryAgain} />}
          {(file || uploadedFile) && !errorUploadError && (
            <Attachment
              file={file}
              uploadedFile={uploadedFile}
              onCancel={onClear}
            />
          )}
        </div>
      </div>
      {error ? (
        <span className="mt-1 text-xs text-input-content-error">{error}</span>
      ) : null}
    </div>
  );
};

export default Uploader;
